import React, { useState } from 'react';
import { Button } from '../../../components/molecules/Btn/Button';
import { Icons, SVGIcon } from '../../../components/atoms/SVG';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { BtnModifier } from '../../../components/molecules/Btn';
import { SharedFilesGlobalDownload } from '../forms/SharedFilesGlobalDownload';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useSharedFilesSelectedItemsContext } from '../context/SharedFilesSelectedItems';
import { SharedFilesGlobalMoveForm } from '../forms/SharedFilesGlobalMoveForm';
import { SharedFilesDeleteForm, SharedFilesMoveForm, SharedFilesPrepareMultiDownloadUrl } from '../SharedFiles.types';
import { SharedFilesGlobalDeleteForm } from '../forms/SharedFilesGlobalDeleteForm';

/**
 * Possible Actions for multiple checked items
 */
type ActionState = 'download' | 'move' | 'delete';

type SharedFilesActionsProps = {
  prepareMultiDownloadUrl: SharedFilesPrepareMultiDownloadUrl;
  moveForm?: SharedFilesMoveForm;
  deleteForm?: SharedFilesDeleteForm;
};

type SharedFilesGlobalActionsButton = {
  title: string;
  onClick: () => void;
  disabled: boolean;
  icon: SVGIcon;
};

function SharedFilesGlobalActionsButton({ title, onClick, disabled, icon }: SharedFilesGlobalActionsButton) {
  return (
    <Button
      title={title}
      tooltipPlacement={TooltipPlacement.TOP}
      modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
      onClick={() => onClick()}
      aria-disabled={disabled}
      type="button"
    >
      <Button.Icon svgSpriteId={icon} text={title} />
    </Button>
  );
}

/**
 * Shared files actions for multiple checked items.
 */
export function SharedFilesGlobalActions({ prepareMultiDownloadUrl, moveForm, deleteForm }: SharedFilesActionsProps) {
  const { t } = useSonTranslation('SharedFiles');
  const { hasSelectedItem } = useSharedFilesSelectedItemsContext();
  const [currentAction, setAction] = useState<ActionState>();
  const closeModal = () => setAction(undefined);

  const switchForm = () => {
    switch (currentAction) {
      case 'download':
        return (
          <SharedFilesGlobalDownload
            closeModal={closeModal}
            headline={t('forms.globalDownload.modalHeadline')}
            prepareMultiDownloadUrl={prepareMultiDownloadUrl}
          />
        );
      case 'move':
        return (
          <SharedFilesGlobalMoveForm
            closeModal={closeModal}
            headline={t('forms.globalMove.modalHeadline')}
            form={moveForm!}
          />
        );
      case 'delete':
        return (
          <SharedFilesGlobalDeleteForm
            closeModal={closeModal}
            headline={t('forms.globalDelete.modalHeadline')}
            form={deleteForm!}
          />
        );
      default:
        return null;
    }
  };

  const globalActionDisabled = !hasSelectedItem();
  return (
    <div className="shared-files__interactions">
      <SharedFilesGlobalActionsButton
        title={t('actions.globalDownload')}
        onClick={() => setAction('download')}
        disabled={globalActionDisabled}
        icon={Icons.DOWNLOAD_ARROW}
      />
      {moveForm && (
        <SharedFilesGlobalActionsButton
          title={t('actions.globalMove')}
          onClick={() => setAction('move')}
          disabled={globalActionDisabled}
          icon={Icons.MOVE}
        />
      )}
      {deleteForm && (
        <SharedFilesGlobalActionsButton
          title={t('actions.globalDelete')}
          onClick={() => setAction('delete')}
          disabled={globalActionDisabled}
          icon={Icons.DELETE_TRASHCAN}
        />
      )}
      {switchForm()}
    </div>
  );
}
